import { find, isNumber } from "lodash";
import { useMemo } from "react";
import { amortizationMethodsListCompanyLoan, FormatNumber } from "utils";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import type { Currency } from "@krea/shared/types/common";
import { formatLenderMonthlyInterestRate } from "@krea/shared/utils/formatNumber";

import { AmortizationMethodEnum } from "@/features/common/enums";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import type { ICompanyLoanBid } from "@/features/lender-responses/types/lender-response";

import type { IBidDetailsResponse } from "../../BidDetails";
import { BidDetailsListItem, EmptyField } from "../../bidHelpers";

interface ILoanBidProps {
  currency: Currency;
  bid: ICompanyLoanBid;
}

export const LoanBid = ({
  bid,
  currency,
}: ILoanBidProps): IBidDetailsResponse => {
  const [formattedCurrencyValue] = useFormatCurrency({
    currency: currency,
  });

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const isInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const amortizationMethodsText = find(
    amortizationMethodsListCompanyLoan,
    (method) => method.value === bid.amortizationMethod,
  );

  const averageMonthlyPayment = bid.averageMonthlyPayment;
  const comparableMonthlyInterestRate = bid.comparableMonthlyInterestRate;

  const summary = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-2 xl:grid xl:grid-cols-4 xl:gap-4">
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Amount"
            value={
              bid.amount ? formattedCurrencyValue(bid.amount) : <EmptyField />
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Repayment time"
            value={
              bid.paybackTimeMonths ? (
                `${bid.paybackTimeMonths} ${bid.paybackTimeMonths > 1 ? "months" : "month"}`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Monthly payment"
            value={
              averageMonthlyPayment ? (
                formattedCurrencyValue(averageMonthlyPayment)
              ) : (
                <EmptyField />
              )
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Effective monthly interest"
            value={
              comparableMonthlyInterestRate ? (
                `${FormatNumber(comparableMonthlyInterestRate)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
      </div>
    );
  }, [
    bid,
    formattedCurrencyValue,
    averageMonthlyPayment,
    comparableMonthlyInterestRate,
  ]);

  const details = useMemo(() => {
    return (
      <>
        <div>
          {!!amortizationMethodsText && !!amortizationMethodsText.label && (
            <BidDetailsListItem
              title="Amortisation type"
              value={amortizationMethodsText.label}
            />
          )}

          {bid.amortizationMethod !== AmortizationMethodEnum.FIXED &&
            isNumber(bid.lenderMonthlyInterest) && (
              <BidDetailsListItem
                title={
                  isInterestRatePerYearActivated
                    ? "Yearly interest rate (shown in contract)"
                    : "Monthly interest rate (shown in contract)"
                }
                value={`${formatLenderMonthlyInterestRate(bid.lenderMonthlyInterest, "TO_YEARLY", isInterestRatePerYearActivated)}% ${isInterestRatePerYearActivated ? "year" : "month"}`}
              />
            )}

          {bid.amortizationMethod === AmortizationMethodEnum.FIXED &&
            !!bid.lenderMonthlyAmount && (
              <BidDetailsListItem
                title="Fixed monthly fee shown in contract"
                value={`${formattedCurrencyValue(bid.lenderMonthlyAmount)}/ month`}
              />
            )}

          {(!!bid.averageMonthlyCost || !!bid.averageMonthlyAmortisation) && (
            <>
              {!!bid.averageMonthlyCost && (
                <BidDetailsListItem
                  title="Interest payment"
                  value={formattedCurrencyValue(bid.averageMonthlyCost)}
                />
              )}
              {!!bid.averageMonthlyAmortisation && (
                <BidDetailsListItem
                  title="Principal payment"
                  value={formattedCurrencyValue(bid.averageMonthlyAmortisation)}
                />
              )}
            </>
          )}

          {!!bid.setupFee && (
            <BidDetailsListItem
              title="Setup fee"
              value={formattedCurrencyValue(bid.setupFee)}
            />
          )}

          {!!bid.postalFee && (
            <BidDetailsListItem
              title="Postal fee (per month)"
              value={formattedCurrencyValue(bid.postalFee)}
            />
          )}

          {!!bid.directDebitFee && (
            <BidDetailsListItem
              title="Direct debit fee (per month)"
              value={formattedCurrencyValue(bid.directDebitFee)}
            />
          )}

          {!!bid.earlyRepaymentFee && (
            <BidDetailsListItem
              title="Cost for early repayment"
              value={
                /^\d+(\.\d+)?$/.test(bid.earlyRepaymentFee.trim())
                  ? formattedCurrencyValue(Number(bid.earlyRepaymentFee))
                  : bid.earlyRepaymentFee
              }
            />
          )}
        </div>
        <div className="mt-4">All fees are included in total loan cost</div>
      </>
    );
  }, [
    bid,
    formattedCurrencyValue,
    amortizationMethodsText,
    isInterestRatePerYearActivated,
  ]);

  return {
    summary,
    details,
  };
};
